import type { SPIntl } from 'contexts/intl/intlContext';
import type { StatusFilter } from 'state/quotes/dashboard/filter/filter.model';
import type { QuoteIndexableData } from 'state/quotes/dashboard/selectors';
import type { GroupColor } from 'state/quotes/dashboard/tiles/tiles.model';
import { camelCaseToWords } from 'utils/string';

export interface FiltersCounts {
  byStatus: Record<StatusFilter, number>;
  byPinned: {
    all: number;
    byGroup: Record<string, number>;
  };

  byClients: Record<string, number>;
}

export function getFilterCountsMap(quotes: QuoteIndexableData[]): FiltersCounts {
  return quotes.reduce<FiltersCounts>(
    (result, quote) => {
      (Object.keys(result.byStatus) as StatusFilter[]).forEach(filter => {
        if (filterQuoteByStatusFilter(quote, filter)) {
          result.byStatus[filter] = result.byStatus[filter] + 1;
        }
      });

      const clientId = quote.clientId || ClientFilter.NO_CLIENT_ID;
      if (clientId) {
        result.byClients[clientId] = (result.byClients[clientId] || 0) + 1;
      }

      const customGroup = quote.customGroup;
      if (customGroup) {
        result.byPinned.byGroup[customGroup] = (result.byPinned.byGroup[customGroup] || 0) + 1;
      }
      if (customGroup || quote.isPinned) {
        result.byPinned.all = result.byPinned.all + 1;
      }
      return result;
    },
    {
      byStatus: {
        all: 0,
        inprogress: 0,
        completed: 0,
        rejected: 0,
      },
      byPinned: {
        all: 0,
        byGroup: {},
      },
      byClients: {},
    },
  );
}

/**
 * @deprecated
 */
export function filterQuote(
  quote: QuoteIndexableData,
  selectedFilter?: StatusFilter,
  selectedCustomGroup?: string,
  selectedClient?: string,
  selectedProduct?: string,
): boolean {
  if (selectedClient) {
    return filterQuoteByClientFilter(quote, selectedClient, selectedProduct);
  } else if (selectedCustomGroup) {
    return filterQuoteByPinnedFilter(quote, selectedCustomGroup);
  } else {
    return filterQuoteByStatusFilter(quote, selectedFilter || 'all');
  }
}

export function filterQuoteByStatusFilter(quote: QuoteIndexableData, selectedFilter: StatusFilter): boolean {
  switch (selectedFilter) {
    case 'all':
      return true;
    case 'completed':
      return quote.completionStatus === 'completed';
    case 'inprogress':
      return quote.completionStatus === 'inprogress';
    case 'rejected':
      return quote.completionStatus === 'rejected';
    default:
      return false;
  }
}

export function filterQuoteByPinnedFilter(quote: QuoteIndexableData, pinnedGroup?: string): boolean {
  return pinnedGroup ? quote.customGroup === pinnedGroup : quote.isPinned || !!quote.customGroup;
}

export function filterQuoteByClientFilter(
  quote: QuoteIndexableData,
  selectedClient?: string,
  selectedProduct?: string,
): boolean {
  let matchingQuote: boolean = false;

  if (!selectedClient) {
    return false;
  }

  if (selectedClient === ClientFilter.NO_CLIENT_ID) {
    matchingQuote = !quote.clientId;
  } else {
    matchingQuote = quote.clientId === selectedClient;
  }

  if (matchingQuote && selectedProduct) {
    matchingQuote = quote.product === selectedProduct;
  }

  return matchingQuote;
}

export type FilterToIconMapper<T extends string> = (filter: T) => string;

export const mapStatusFilterToIcon: FilterToIconMapper<StatusFilter> = filter => {
  switch (filter) {
    case 'all':
      return 'rule';
    case 'inprogress':
      return 'autorenew';
    case 'completed':
      return 'playlist_add_check';
    case 'rejected':
      return 'error_outline';
    default:
      return '';
  }
};

export class ClientFilter {
  static NO_CLIENT_ID = 'no_client';

  constructor(
    public id: string,
    public name: string,
    public count: number,
    public products: ProductFilter[],
  ) {}
}

export class ProductFilter {
  constructor(
    public id: string,
    public name: string,
    public count: number,
  ) {}
}

export function buildClientsFilterTree(
  quotes: QuoteIndexableData[],
  noClientLabel: string,
  formatMessage: SPIntl['formatMessage'],
): ClientFilter[] {
  const clientNames: Record<string, string> = {};
  const clientsCounts: Record<string, number> = {};
  const productNames: Record<string, string> = {};
  const clientsProducts: Record<string, string[]> = {};

  quotes.forEach(quote => {
    const clientId = quote.clientId || ClientFilter.NO_CLIENT_ID;

    if (!clientNames[clientId]) {
      clientNames[clientId] = quote.clientName || quote.clientId || noClientLabel;
    }
    if (!productNames[quote.product]) {
      productNames[quote.product] = formatProduct(quote.product, formatMessage);
    }

    if (clientsProducts[clientId]) {
      clientsProducts[clientId].push(quote.product);
    } else {
      clientsProducts[clientId] = [quote.product];
    }

    if (clientsCounts[clientId]) {
      clientsCounts[clientId] = clientsCounts[clientId] + 1;
    } else {
      clientsCounts[clientId] = 1;
    }
  });

  return Object.entries(clientNames)
    .reduce<ClientFilter[]>((clients, [clientId, clientName]) => {
      const clientProductsCounts = clientsProducts[clientId].reduce<Record<string, number>>((map, product) => {
        map[product] = (map[product] || 0) + 1;
        return map;
      }, {});
      const clientProductFilters = Object.entries(clientProductsCounts)
        .map(([product, count]) => new ProductFilter(product, productNames[product], count))
        .sort((productFilter1, productFilter2) => productFilter1.name.localeCompare(productFilter2.name));

      clients.push(new ClientFilter(clientId, clientName, clientsCounts[clientId], clientProductFilters));
      return clients;
    }, [])
    .sort((client1, client2) => client1.name.localeCompare(client2.name));
}

export function formatProduct(product: string, formatMessage: SPIntl['formatMessage']): string {
  return formatMessage({ id: `field.${product}` as any, defaultMessage: camelCaseToWords(product) });
}

export type PinnedGroupColors = Record<string, GroupColor | undefined>;
